import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import { privateAxios } from "../utils/axios";
import {
  CubeIcon,
  UsersIcon,
  ShoppingCartIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

const KPI_CONFIG = [
  { label: "Total Products",  key: "totalProducts",      icon: CubeIcon,         iconBg: "bg-blue-50",   iconColor: "text-blue-500",   border: "border-t-blue-400" },
  { label: "Total Suppliers", key: "totalSuppliers",     icon: UsersIcon,        iconBg: "bg-violet-50", iconColor: "text-violet-500", border: "border-t-violet-400" },
  { label: "Total Purchase",  key: "totalPurchaseAmount",icon: ShoppingCartIcon, iconBg: "bg-emerald-50",iconColor: "text-emerald-500", border: "border-t-emerald-400", currency: true },
  { label: "Total Expenses",  key: "totalExpensesAmount",icon: ChartBarIcon,     iconBg: "bg-rose-50",   iconColor: "text-rose-500",   border: "border-t-rose-400",   currency: true },
];

function KpiCard({ label, value, icon: Icon, iconBg, iconColor, border }) {
  return (
    <article className={`bg-white rounded-xl border border-gray-100 border-t-2 ${border} shadow-sm p-5 flex items-center gap-4`}>
      <div className={`p-2.5 rounded-lg ${iconBg} flex-shrink-0`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div>
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-semibold text-gray-800 mt-0.5">{value ?? "—"}</p>
      </div>
    </article>
  );
}

function Dashboard() {
  const [kpiData, setKpiData] = useState(null);
  const [monthlyTrends, setMonthlyTrends] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [recentTxns, setRecentTxns] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      privateAxios.get("/dashboard-info/get-dashboard-data"),
      privateAxios.get("/dashboard-info/get-monthly-trends"),
      privateAxios.get("/dashboard-info/get-low-stock-products"),
      privateAxios.get("/dashboard-info/get-recent-transactions"),
    ])
      .then(([kpi, trends, stock, txns]) => {
        if (kpi.data.success) setKpiData(kpi.data.data);
        if (trends.data.success) setMonthlyTrends(trends.data.data);
        if (stock.data.success) setLowStock(stock.data.data);
        if (txns.data.success) setRecentTxns(txns.data.data);
      })
      .catch((err) => console.error("Dashboard fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  const chartOptions = {
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
    plotOptions: { bar: { borderRadius: 4, columnWidth: "48%" } },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories: monthlyTrends?.labels ?? [],
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: "#9ca3af", fontSize: "12px" } },
    },
    yaxis: {
      labels: {
        formatter: (val) => `${(val / 1000).toFixed(0)}K`,
        style: { colors: "#9ca3af", fontSize: "12px" },
      },
    },
    colors: ["#6366f1", "#34d399"],
    legend: { position: "top", labels: { colors: "#374151" } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} TK` } },
    grid: { borderColor: "#f3f4f6", strokeDashArray: 4 },
  };

  const chartSeries = [
    { name: "Purchases", data: monthlyTrends?.purchases ?? [] },
    { name: "Expense", data: monthlyTrends?.expense ?? [] },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 col-span-12 lg:col-span-10">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-indigo-500" />
      </div>
    );
  }

  const kpiValues = {
    totalProducts: kpiData?.totalProducts,
    totalSuppliers: kpiData?.totalSuppliers,
    totalPurchaseAmount: kpiData?.totalPurchaseAmount != null ? `${kpiData.totalPurchaseAmount.toLocaleString()} TK` : null,
    totalExpensesAmount: kpiData?.totalExpensesAmount != null ? `${kpiData.totalExpensesAmount.toLocaleString()} TK` : null,
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen col-span-12 lg:col-span-10">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {KPI_CONFIG.map((cfg) => (
          <KpiCard
            key={cfg.key}
            label={cfg.label}
            value={kpiValues[cfg.key]}
            icon={cfg.icon}
            iconBg={cfg.iconBg}
            iconColor={cfg.iconColor}
            border={cfg.border}
          />
        ))}
      </div>

      {/* Monthly Trends Chart */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-6">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
          Monthly Trends — Last 6 Months
        </h2>
        <ReactApexChart
          options={chartOptions}
          series={chartSeries}
          type="bar"
          height={280}
        />
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {/* Recent Purchases */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-indigo-400 inline-block" />
              Recent Purchases
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-xs uppercase border-b border-gray-100">
                  <th className="pb-2 text-left font-medium">Invoice</th>
                  <th className="pb-2 text-left font-medium">Date</th>
                  <th className="pb-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentTxns?.recentPurchases?.length > 0 ? (
                  recentTxns.recentPurchases.map((p) => (
                    <tr key={p._id} className="border-b border-gray-50 last:border-0 hover:bg-indigo-50/40">
                      <td className="py-2.5 font-medium text-gray-700">
                        {p.invoiceNumber || p.memoNumber || "—"}
                      </td>
                      <td className="py-2.5 text-gray-400">
                        {moment(p.purchaseDate).format("DD MMM YYYY")}
                      </td>
                      <td className="py-2.5 text-right font-semibold text-indigo-600">
                        {p.totalAmount.toLocaleString()} TK
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-sm text-gray-300">
                      No purchases found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Recent Expense */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
              Recent Expense
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-xs uppercase border-b border-gray-100">
                  <th className="pb-2 text-left font-medium">Invoice</th>
                  <th className="pb-2 text-left font-medium">Date</th>
                  <th className="pb-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentTxns?.recentExpense?.length > 0 ? (
                  recentTxns.recentExpense.map((s) => (
                    <tr key={s._id} className="border-b border-gray-50 last:border-0 hover:bg-emerald-50/40">
                      <td className="py-2.5 font-medium text-gray-700">
                        {s.invoiceNumber || s.receiverName || "—"}
                      </td>
                      <td className="py-2.5 text-gray-400">
                        {moment(s.expenseDate).format("DD MMM YYYY")}
                      </td>
                      <td className="py-2.5 text-right font-semibold text-emerald-600">
                        {s.totalAmount.toLocaleString()} TK
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-sm text-gray-300">
                      No expense found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Low Stock Alert */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-1.5">
            <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-500" />
            Low Stock Alert
          </h3>
          {lowStock.length > 0 ? (
            lowStock.map((item) => (
              <div
                key={item._id}
                className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0"
              >
                <div>
                  <p className="text-sm font-medium text-gray-700">{item.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{item.sku}</p>
                </div>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    item.stock === 0
                      ? "bg-red-100 text-red-600"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {item.stock === 0 ? "Out of stock" : `${item.stock} left`}
                </span>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-300 text-center py-8">
              All products well stocked
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
